'use client';

import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { useToast } from '@/components/ui/use-toast';
import { mercadoPagoService } from '@/api/mercadopago/mercadoPagoApi';
import { userService } from '@/api/user/userApi';
import { LoaderCircle } from 'lucide-react';

// Storage key for tracking processed authorization codes
const PROCESSED_CODE_KEY = 'mp_auth_processed_code';

export function MercadoPagoOAuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  useEffect(() => {
    const handleAuthorization = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');

      if (!code || !state) {
        toast({
          title: 'Error',
          description:
            'Error en la autorización. Por favor, intente nuevamente.',
          variant: 'destructive',
        });
        navigate('/');
        return;
      }

      // Check if this specific code has already been processed
      const processedCode = sessionStorage.getItem(PROCESSED_CODE_KEY);
      if (processedCode === code) {
        // Already processed, just wait for the first request to complete
        return;
      }

      // Mark this code as being processed
      sessionStorage.setItem(PROCESSED_CODE_KEY, code);

      try {
        const response = await mercadoPagoService.handleAuthorization(
          code,
          state,
        );
        if (response) {
          // Update user information to sync hasMercadoPagoCredentials
          try {
            await userService.refreshCurrentUser();
          } catch (userError) {
            console.error('Error updating user information:', userError);
            // Don't block the success flow if user update fails
          }

          toast({
            title: 'Éxito',
            description: 'Mercado Pago ha sido autorizado exitosamente.',
            variant: 'default',
          });
          navigate('/');
        }
      } catch (error) {
        console.error('Error al obtener el token de acceso:', error);
        toast({
          title: 'Error',
          description:
            'Hubo un error al procesar la autorización. Por favor, intenta nuevamente.',
          variant: 'destructive',
        });
        navigate('/');
      } finally {
        // Clean up the processed code from storage
        sessionStorage.removeItem(PROCESSED_CODE_KEY);
      }
    };

    handleAuthorization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background">
      <div className="space-y-4 text-center">
        <h2 className="flex items-center justify-center gap-4 text-2xl font-semibold text-foreground">
          <LoaderCircle className="h-10 w-10 animate-spin" />
          Procesando autorización
        </h2>
        <p className="text-muted-foreground">
          Por favor, espere mientras completamos el proceso...
        </p>
      </div>
      <Toaster />
    </div>
  );
}
